import { StyledEngineProvider } from '@mui/material/styles'
import { QueryClientProvider, useQueryClient } from '@tanstack/react-query'
import { Suspense, lazy, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { HelmetProvider } from 'react-helmet-async'
import { Navigate, Outlet } from 'react-router'
import { ToastContainer } from 'react-toastify'
import { SWRConfig } from 'swr'
import { MembershipPurplePinkGradient } from 'src/assets/SvgGradients'
import { applyDebugSettingsFromQs } from 'src/components/Admin/DebugWidget/applyDebugFromQs'
import { ClientBoot } from 'src/components/ClientBoot'
import ColorModeProvider from 'src/components/Common/ColorModeProvider'
import { SlotProvider } from 'src/components/Common/slot'
import { DayjsLocaleEffectApplier } from 'src/components/DayjsLocaleEffectApplier'
import LoadingPage from 'src/components/LoadingPage'
import {
  DefaultHelmetTags,
  DefaultMetaCanonicalURL,
} from 'src/components/MetaTags'
import { UserPropertiesTracker } from 'src/components/UserPropertiesTracker'
import { useSsrRendererServiceExperiment } from 'src/experiments/experiments/2601/ssrRendererService'
import { CouponAuthHandler } from 'src/features/Coupon/Regular/states'
import { GoogleOneTap } from 'src/features/GoogleOneTap'
import { RenderTracker } from 'src/features/RenderTracker/RenderTracker'
import { UANewbiePackDailyClaimSlot } from 'src/features/UAPromotion/UANewbiePackPromotions'
import { ApolloClientProvider } from 'src/services/apollo/provider'
import { UserPreferenceProvider } from 'src/services/helpers/preference'
import { createQueryClient } from 'src/services/query'
import { globalSWRConfig } from 'src/services/swr'
import { setHeaderBanner, setHeaderCreditPill } from 'src/stores/banner'
import {
  useAdClickCapture,
  useAttributionIdentify,
} from 'src/utils/attribution'
import {
  UserInfoChangeHandler,
  useInitialQueryStringHandler,
} from 'src/utils/auth'
import { DynamicCreateElementContainer } from 'src/utils/dynamic'
import { ExperimentVariantsBootstrap } from 'src/utils/experimentNew'
import { primeFingerprint } from 'src/utils/fingerprint'
import {
  IntercomController,
  IntercomMessengerProvider,
} from 'src/utils/intercom'
import { type AppRouteHandle, useMatches } from 'src/utils/router/route'
import { setVisibleDimensions } from 'src/utils/setVisibleDimensions'
import { cleanupObsoletedLocalStorageItems } from 'src/utils/storageCleanup'
import { endEventGroup, useAutoTrackPageView } from 'src/utils/tracker'
import { useUserIsRevivable } from 'src/utils/user'
/* Preludes from `src/main.tsx`. */
import 'src/i18n/i18next'
import { OnboardingPendingWatcher } from 'src/features/NewUserOnboarding/components/OnboardingPendingWatcher'
import 'src/index.css'
import 'src/utils/jsBridge/domHook' // Initialize JS Bridge early
import 'src/utils/sentry'
import 'src/utils/swClient'
import { startI18nUsageReporter } from 'src/i18n/i18nUsageReporter'
import LoadGrowthEvents from '~/features/GrowthEvent/common/framework/LoadGrowthEvents'
import { LocaleSuggestionToast } from '~/features/Locale/LocaleSuggestionToast'
import { useMe } from '~/services/helpers/user'

// Lazy-loaded: heavy transitive vendor deps not needed for first paint
const DebugWidget = lazy(() => import('src/components/Admin/DebugWidget'))
const OnboardingDebugLauncher = lazy(
  () =>
    import('src/features/NewUserOnboarding/components/OnboardingDebugLauncher'),
)
const ForceRefreshChecker = lazy(() =>
  import('src/components/ForceRefreshChecker').then(m => ({
    default: m.ForceRefreshChecker,
  })),
)
/* Preludes from `src/App.tsx`. */
setVisibleDimensions()
cleanupObsoletedLocalStorageItems()
applyDebugSettingsFromQs()
primeFingerprint()
startI18nUsageReporter()

const Root = () => {
  useInitialQueryStringHandler()
  useAutoTrackPageView()
  useAdClickCapture()

  const [queryClient] = useState(() => createQueryClient())

  useEffect(() => {
    // Ensure all remained event groups being cleared after a page reload.
    // Wrapping in an effect is to ensure the first page_view event being with
    // an event group.
    endEventGroup(true)
  }, [])

  return (
    <ApolloClientProvider>
      <QueryClientProvider client={queryClient}>
        <QueryCacheInvalidator />
        <SWRConfig value={globalSWRConfig}>
          <UserPreferenceProvider>
            <ExperimentVariantsBootstrap />
            <SsrRendererServiceExposure />
            <StyledEngineProvider>
              <ColorModeProvider>
                <IntercomMessengerProvider>
                  <HelmetProvider>
                    <DefaultHelmetTags />
                    <Suspense fallback={<LoadingPage fullscreen />}>
                      <DefaultMetaCanonicalURL />
                      <NavigationListener />
                      <SlotProvider>
                        <LoadGrowthEvents />
                        <UANewbiePackDailyClaimSlot />
                        <DynamicCreateElementContainer>
                          <LocaleSuggestionToast />
                          <Outlet />
                        </DynamicCreateElementContainer>
                      </SlotProvider>
                      <RenderTracker />
                      <UserPropertiesTracker />
                      <IntercomController />
                      <NavigateToUserRevival />
                      {createPortal(
                        // 1400 is needed for MUI Modals
                        <ToastContainer
                          stacked
                          className="z-[1400] top-16"
                          closeOnClick
                        />,
                        document.body,
                      )}
                      <ClientBoot />
                      <Suspense fallback={null}>
                        <DebugWidget />
                      </Suspense>
                      <Suspense fallback={null}>
                        <OnboardingDebugLauncher />
                      </Suspense>
                      <OnboardingPendingWatcher />
                      <DayjsLocaleEffectApplier />
                      <MembershipPurplePinkGradient />
                      <UserInfoChangeHandler />
                      <AttributionIdentify />
                      <CouponAuthHandler />
                      <GoogleOneTap />
                      <Suspense fallback={null}>
                        <ForceRefreshChecker />
                      </Suspense>
                    </Suspense>
                  </HelmetProvider>
                </IntercomMessengerProvider>
              </ColorModeProvider>
            </StyledEngineProvider>
          </UserPreferenceProvider>
        </SWRConfig>
      </QueryClientProvider>
    </ApolloClientProvider>
  )
}

export default Root

function NavigationListener() {
  const state: Record<string, any> = {}
  const matches = useMatches()

  for (const route of matches) {
    const handle = route.handle as AppRouteHandle

    if (handle.hideMembershipBanner != null) {
      state.hideMembershipBanner = handle.hideMembershipBanner
    }

    if (handle.showCreditPill != null) {
      state.showCreditPill = handle.showCreditPill
    }
  }

  setHeaderBanner(!state.hideMembershipBanner)
  setHeaderCreditPill(Boolean(state.showCreditPill))

  return null
}

function AttributionIdentify() {
  useAttributionIdentify()
  return null
}

function NavigateToUserRevival() {
  const canRevive = useUserIsRevivable()

  if (canRevive) return <Navigate to="/user-revival" />
  return null
}

function SsrRendererServiceExposure() {
  useSsrRendererServiceExperiment()
  return null
}

function QueryCacheInvalidator() {
  const { me, loading } = useMe()
  const queryClient = useQueryClient()

  // Reset login-required queries from TanStack Query to initial state when
  // user logs in/out. See the longer explanation on the mirror copy in
  // `src/components/RootRuntimeInner.tsx` — we skip the very first
  // resolution of `useMyInfo()` so SSR-seeded react-query entries
  // survive the hydration commit instead of being wiped, which would
  // surface as a loading flash even though the SSR HTML already
  // shipped the populated grid.
  const previousMeIdRef = useRef<string | undefined>(undefined)
  const baselineCapturedRef = useRef(false)

  useEffect(() => {
    if (loading) return
    if (!baselineCapturedRef.current) {
      baselineCapturedRef.current = true
      previousMeIdRef.current = me?.id
      return
    }
    if (previousMeIdRef.current === me?.id) return
    previousMeIdRef.current = me?.id
    queryClient.resetQueries({
      predicate: query =>
        query.options.meta?.dependsOn?.includes('login') ?? false,
    })
  }, [me?.id, loading, queryClient])

  return null
}
